import {
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  Grid,
  HeaderInput,
  HeaderTemplate,
  KeyValue,
  Pill,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { configurationTypeData } from '../../../modules/admin/logic/dropDownOption';
import { useGetNotificationFeature } from '../../../api-refactored/manage-notifications/useGetNotificationFeature';
import RouteKey from '../../../routes/key';
import RecipientDataTable from './RecipientDataTable';
import {
  NotificationViewDetailsResponse,
  useGetNotificationViewDetails,
} from '../../../api-refactored/manage-notifications/useGetNotificationViewDetails';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
({
  dataContainer: {
    marginBottom: theme.spacing(6),
  },
  select: {
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  button: {
    marginBottom: theme.spacing(7),
  },
}));

const { admin } = RouteKey.child;
const { manageNotifications } = RouteKey.admin.child;

const displayPill = (data: NotificationViewDetailsResponse) => {
  const { status } = data;
  if (status === 'Sent') {
    return <Pill variant="positive" label={status} />;
  }
  if (status === 'Sending') {
    return <Pill variant="info" label={status} />;
  }
  return <>-</>;
};

const ViewNotificationDetails = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { uuid } = useParams<{ uuid: string }>();
  const { state: locationState } = useLocation<{ featureValue: string } | undefined>();

  const history = useHistory<{ featureValue: string; page: number; pageSize: number }>();

  const dataFromNotificationListing = history?.location?.state;

  const [featureValue] = useState<string>(locationState?.featureValue || '');

  const [featureDisplay, setFeatureDisplay] = useState<string>('');

  const {
    data: notificationFeature,
    isLoading: isLoadingNotificationFeature,
  } = useGetNotificationFeature();

  const {
    data: notificationViewDetails,
    isLoading: isLoadingNotificationViewDetails,
  } = useGetNotificationViewDetails({
    uuid,
    notificationType: locationState!.featureValue,
  });

  useEffect(() => {
    if (featureValue === 'EMAIL') {
      setFeatureDisplay('Email');
    } else if (featureValue === 'IN_APP') {
      setFeatureDisplay('In-App');
    } else {
      setFeatureDisplay(featureValue);
    }
  }, [featureValue]);

  if (isLoadingNotificationFeature || isLoadingNotificationViewDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handleBackToNotificationsList = () =>
    history.push(`/${admin}/${manageNotifications.route}`, {
      featureValue,
      page: dataFromNotificationListing.page,
      pageSize: dataFromNotificationListing.pageSize,
    });

  return (
    <>
      <TitleHeader title={t('announcementsNotifications->>Listing.ManageNotifications')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('announcementsNotifications->>Listing.Feature')}
          input={
            notificationFeature &&
            configurationTypeData(notificationFeature) && (
              <CustomSelect
                id="inputSelect-feature"
                isHeader
                menuItems={configurationTypeData(notificationFeature)}
                value={featureValue}
                disabled
                className={classes.select}
              />
            )
          }
          topBottom={false}
        />
      </HeaderTemplate>
      {notificationViewDetails && (
        <>
          <ContentTemplate isDividerBottom isContinuable>
            <SectionHeader
              title={t('announcementsNotifications->>Listing.FeatureNotifications', {
                feature: featureDisplay,
              })}
            />
            <Box className={classes.dataContainer}>
              <Grid container>
                <Grid xs={12} sm={6}>
                  <KeyValue
                    keyText={t('announcementsNotifications->>ViewDetails.Status')}
                    valueText={displayPill(notificationViewDetails)}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <KeyValue
                    keyText={t('announcementsNotifications->>ViewDetails.DateTimeSent')}
                    valueText={stringToLongDateAndShortTime(notificationViewDetails.sentDateTime)}
                  />
                </Grid>
              </Grid>
              <KeyValue
                keyText={t('announcementsNotifications->>ViewDetails.SentBy')}
                valueText={notificationViewDetails.sentBy}
              />
            </Box>
          </ContentTemplate>
          <ContentTemplate isDividerBottom continuous isContinuable>
            <Box className={classes.dataContainer}>
              <Typography className={classes.sectionTitle} variant="h3">
                {t('announcementsNotifications->>ViewDetails.NotificationDetails')}
              </Typography>
              <KeyValue
                keyText={
                  featureDisplay === 'SMS'
                    ? t('announcementsNotifications->>ViewDetails.Message')
                    : t('announcementsNotifications->>ViewDetails.Title')
                }
                valueText={
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {notificationViewDetails?.title || '-'}
                  </Typography>
                }
              />
              {notificationViewDetails.message && (
                <KeyValue
                  keyText={t('announcementsNotifications->>ViewDetails.Message')}
                  valueText={<>{parse(notificationViewDetails.message) || '-'}</>}
                />
              )}
            </Box>
          </ContentTemplate>
          <ContentTemplate continuous type="full">
            <Box className={classes.dataContainer}>
              <Typography className={classes.sectionTitle} variant="h3">
                {t('announcementsNotifications->>ViewDetails.Recipients')}
              </Typography>
              {!notificationViewDetails.individualRecipient ? (
                <>
                  <KeyValue
                    keyText={t('announcementsNotifications->>ViewDetails.RecipientType')}
                    valueText={notificationViewDetails.groupRecipient.recipientType}
                  />
                  {notificationViewDetails.groupRecipient.recipientType === 'Candidate' && (
                    <>
                      <KeyValue
                        keyText={t('announcementsNotifications->>ViewDetails.ExamLevel')}
                        valueText={notificationViewDetails.groupRecipient.examLevel}
                      />
                      <KeyValue
                        keyText={t('announcementsNotifications->>ViewDetails.CandidateType')}
                        valueText={notificationViewDetails.groupRecipient.candidateType}
                      />
                      <KeyValue
                        keyText={t('announcementsNotifications->>ViewDetails.School')}
                        valueText={notificationViewDetails.groupRecipient.school}
                      />
                    </>
                  )}
                  {notificationViewDetails.groupRecipient.recipientType === 'Coordinator' && (
                    <KeyValue
                      keyText={t('announcementsNotifications->>ViewDetails.CentreSchool')}
                      valueText={notificationViewDetails.groupRecipient.school}
                    />
                  )}
                </>
              ) : (
                <RecipientDataTable
                  individualRecipients={notificationViewDetails?.individualRecipients}
                  featureValue={featureValue}
                />
              )}
            </Box>
            <Button
              id="button-view-notification-back-to-notifications-list"
              className={classes.button}
              variant="outlined"
              onClick={handleBackToNotificationsList}
            >
              {t('announcementsNotifications->>ViewDetails.BackToNotificationsList')}
            </Button>
          </ContentTemplate>
        </>
      )}
    </>
  );
};

export default ViewNotificationDetails;
